import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Mail, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ManageEmailConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymId: string;
  gymName: string;
}

export function ManageEmailConfigDialog({
  open,
  onOpenChange,
  gymId,
  gymName,
}: ManageEmailConfigDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const [senderEmail, setSenderEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isDefault, setIsDefault] = useState(true);

  useEffect(() => {
    if (open && gymId) {
      fetchEmailConfig();
    }
  }, [open, gymId]);

  const fetchEmailConfig = async () => {
    setIsFetching(true);
    try {
      const { data, error } = await supabase.functions.invoke('settings-management', {
        body: {
          action: 'get_email_config',
          target_gym_id: gymId
        }
      });

      if (error) throw error;

      if (data?.config) {
        setSenderEmail(data.config.sender_email || "");
        setSenderName(data.config.sender_name || "");
        setIsDefault(data.is_default || false);
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
      toast.error(t("emailConfig.loadError"));
    } finally {
      setIsFetching(false);
    }
  };

  const handleSave = async () => {
    if (!senderEmail) {
      toast.error(t("emailConfig.senderRequired"));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(senderEmail)) {
      toast.error(t("emailConfig.invalidFormat"));
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('settings-management', {
        body: {
          action: 'update_email_config',
          target_gym_id: gymId,
          sender_email: senderEmail,
          sender_name: senderName || "VB20 Tunisie"
        }
      });

      if (error) throw error;

      toast.success(t("emailConfig.updated"));
      setIsDefault(false);
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating email config:', error);
      toast.error(error.message || t("emailConfig.updateError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {t("emailConfig.title")}
          </DialogTitle>
          <DialogDescription>
            {t("emailConfig.description")} <strong>{gymName}</strong>
          </DialogDescription>
        </DialogHeader>

        {isFetching ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {isDefault && (
              <div className="flex items-start gap-2 p-3 bg-status-info border border-status-info-border rounded-lg text-sm text-status-info-foreground">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">{t("emailConfig.defaultConfig")}</p>
                  <p>{t("emailConfig.defaultConfigDesc")}</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="senderEmail">{t("emailConfig.senderEmail")} *</Label>
              <Input
                id="senderEmail"
                type="email"
                placeholder="no-reply@exemple.com"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t("emailConfig.mustBeVerified")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="senderName">{t("emailConfig.displayName")}</Label>
              <Input
                id="senderName"
                type="text"
                placeholder="VB20 Tunisie"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t("emailConfig.displayNameDesc")}
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t("common:cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isLoading || isFetching}>
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t("common:save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
